import { useState } from 'react';
import { Link } from '../../components/base/Link';
import { asset } from '../../lib/assets';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import FramePair from '../../components/feature/FramePair';
import Footer from '../../components/feature/Footer';

export default function ImageRedaction() {
  const { t } = useTranslation();
  const [selectedStyle, setSelectedStyle] = useState<'blur' | 'pixelate' | 'mask'>('blur');

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* 科技背景 - 保持所有动画效果不变 */}
        <div className="absolute inset-0 bg-brand-navy">
          <img src={asset('/images/real/mall-9-after.webp')} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/75 to-blue-950/70"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('imageRedaction.heroTitle')}
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              {t('imageRedaction.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* 功能亮点 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('home.featuresTitle')}</h2>
            <p className="text-lg text-gray-600">{t('home.featuresSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('imageRedaction.feature1Title')}</h3>
              <p className="text-gray-600">{t('imageRedaction.feature1Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-car-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('imageRedaction.feature2Title')}</h3>
              <p className="text-gray-600">{t('imageRedaction.feature2Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-edit-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('imageRedaction.feature3Title')}</h3>
              <p className="text-gray-600">{t('imageRedaction.feature3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 效果展示 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('imageRedaction.effectTitle')}</h2>
            <p className="text-lg text-gray-600">{t('imageRedaction.effectSubtitle')}</p>
          </div>
          
          {/* 打码方式选择 */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg p-1 shadow-md flex">
              <button
                onClick={() => setSelectedStyle('blur')}
                className={`px-6 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  selectedStyle === 'blur' 
                    ? 'bg-brand-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('imageRedaction.blurMode')}
              </button>
              <button
                onClick={() => setSelectedStyle('pixelate')}
                className={`px-6 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  selectedStyle === 'pixelate' 
                    ? 'bg-brand-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('imageRedaction.pixelateMode')}
              </button>
              <button
                onClick={() => setSelectedStyle('mask')}
                className={`px-6 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  selectedStyle === 'mask' 
                    ? 'bg-brand-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('imageRedaction.maskMode')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* 原图 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">{t('imageRedaction.originalTitle')}</h3>
                <p className="text-gray-600 text-sm mt-1">{t('imageRedaction.originalDesc')}</p>
              </div>
              <div className="p-6">
                <img 
                  src={asset('/images/real/style-before.webp')}
                  alt="原始图片"
                  className="w-full rounded-lg object-cover object-top"
                />
              </div>
            </div>

            {/* 处理后 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">{t('imageRedaction.processedTitle')}</h3>
                <p className="text-gray-600 text-sm mt-1">
                  {selectedStyle === 'blur' && t('imageRedaction.blurDesc')}
                  {selectedStyle === 'pixelate' && t('imageRedaction.pixelateDesc')}
                  {selectedStyle === 'mask' && t('imageRedaction.maskDesc')}
                </p>
              </div>
              <div className="p-6">
                <img 
                  src={selectedStyle === 'blur' ? asset('/images/real/style-blur.webp') :
                    selectedStyle === 'pixelate' ? asset('/images/real/style-mosaic.webp') :
                    asset('/images/real/style-solid.webp')}
                  alt="处理后图片"
                  className="w-full rounded-lg object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 应用示例：AI 原图 → 真实输出 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('imageRedaction.examplesTitle')}</h2>
            <p className="text-lg text-gray-600">{t('imageRedaction.examplesSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden p-5">
              <FramePair before={asset('/images/real/ai-crowd-before.webp')} after={asset('/images/real/ai-crowd-after.webp')} beforeLabel={t('common.before')} afterLabel={t('common.after')} alt={t('imageRedaction.example1Title')} />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">{t('imageRedaction.example1Title')}</h3>
              <p className="mt-1 text-gray-600 text-sm">{t('imageRedaction.example1Desc')}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden p-5">
              <FramePair before={asset('/images/real/ai-street-before.webp')} after={asset('/images/real/ai-street-after.webp')} beforeLabel={t('common.before')} afterLabel={t('common.after')} alt={t('imageRedaction.example2Title')} />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">{t('imageRedaction.example2Title')}</h3>
              <p className="mt-1 text-gray-600 text-sm">{t('imageRedaction.example2Desc')}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden p-5">
              <FramePair before={asset('/images/real/ai-traffic-before.webp')} after={asset('/images/real/ai-traffic-after.webp')} beforeLabel={t('common.before')} afterLabel={t('common.after')} alt={t('imageRedaction.example3Title')} />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">{t('imageRedaction.example3Title')}</h3>
              <p className="mt-1 text-gray-600 text-sm">{t('imageRedaction.example3Desc')}</p>
            </div>
          </div>
          <p className="mt-8 text-center text-sm text-gray-500">{t('common.aiNote')}</p>
        </div>
      </section>

      {/* 技术特性 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('imageRedaction.techTitle')}</h2>
            <p className="text-lg text-gray-600">{t('imageRedaction.techSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-focus-3-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('imageRedaction.tech1Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('imageRedaction.tech1Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-speed-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('imageRedaction.tech2Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('imageRedaction.tech2Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-image-edit-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('imageRedaction.tech3Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('imageRedaction.tech3Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-settings-3-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('imageRedaction.tech4Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('imageRedaction.tech4Desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* API 集成 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('imageRedaction.apiTitle')}</h2>
            <p className="text-lg text-gray-600">{t('imageRedaction.apiSubtitle')}</p>
          </div>
          
          <div className="mx-auto max-w-3xl rounded-xl border border-brand-100 bg-brand-50 p-8 text-center">
            <Link to="/pricing#api-pilot" className="font-semibold text-brand-700 underline">{t('footer.apiDocs')}</Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('imageRedaction.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('imageRedaction.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://damaya.cn" className="bg-white text-brand-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              {t('imageRedaction.ctaButton1')}
            </a>
            <Link to="/pricing#contact" className="bg-brand-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-800 transition-colors border border-brand-600 whitespace-nowrap cursor-pointer">
              {t('imageRedaction.ctaButton2')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
